import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { GeliverSender, GeliverSettingsView, ShippingMethodRow } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

export const CARRIERS = [
  'yurtici',
  'aras',
  'mng',
  'ptt',
  'surat',
  'hepsijet',
  'kolaygelsin',
  'ups',
  'other',
] as const;

/** Kargo yöntemleri: sepette seçilen kargonun ücreti (sabit veya ağırlık kademesi), ücretsiz kargo sınırı. */
export function ShippingMethodsPage() {
  const t = useT();
  const money = useMoney();
  const { can } = usePermissions();
  const manage = can('shipping.method.manage');
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['shipping-methods', storeId],
    queryFn: () => api.get<ShippingMethodRow[]>(`/v1/admin/shipping-methods?storeId=${storeId}`),
    enabled: !!storeId,
  });
  const [edit, setEdit] = useState<ShippingMethodRow | 'new' | null>(null);
  // Yeni yöntemin varsayılan para birimi: mağazanın varsayılan pazarı (F7-11).
  const current = stores.find((s) => s.id === storeId);
  const storeCurrency = current?.markets.find((m) => m.id === current.defaultMarketId)?.currency ?? 'TRY';
  // Pazar ülkeleri (F7-13): yöntem belirli ülkelerle sınırlandırılabilir.
  const marketCountries = [...new Set((current?.markets ?? []).map((m) => m.countryCode))].sort();
  const countryName = useCountryName();
  const del = useApiMutation((id: string) => api.del(`/v1/admin/shipping-methods/${id}`), {
    invalidate: [
      ['shipping-methods', storeId],
      ['readiness', storeId],
    ],
    success: t('common.deleted'),
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame
      helpKey="page.shipping"
      actions={
        manage &&
        storeId && (
          <div className="flex items-center gap-1">
            <Button variant="primary" onClick={() => setEdit('new')}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.shipping.add')}
            </Button>
            <HelpTip k="shipping.method" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {!q.data ? (
            <Spinner label={t('common.loading')} />
          ) : !q.data.length ? (
            <EmptyState title={t('admin.shipping.empty')} description={t('admin.shipping.emptyLead')} />
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {q.data.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {m.name}
                      {m.carrier && (
                        <span className="font-normal text-ink-3">
                          {' '}
                          · {t(`admin.shipping.carriers.${m.carrier}`)}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-ink-2">
                      {[
                        m.rateType === 'flat'
                          ? m.priceMinor === 0
                            ? t('admin.shipping.freeAlways')
                            : money(m.priceMinor, m.currency)
                          : t('admin.shipping.byWeight', { count: m.weightTiers?.length ?? 0 }),
                        m.freeOverMinor !== null
                          ? t('admin.shipping.freeOverShort', { amount: money(m.freeOverMinor, m.currency) })
                          : null,
                        m.currency !== storeCurrency
                          ? t('admin.shipping.otherCurrency', { currency: m.currency })
                          : null,
                        m.countryCodes.length
                          ? t('admin.shipping.countriesShort', {
                              list: m.countryCodes.map(countryName).join(', '),
                            })
                          : null,
                        m.codAvailable ? t('admin.shipping.codShort') : null,
                        t(`admin.shipping.audiences.${m.audience}`),
                        m.estimatedDays ? t('admin.shipping.daysShort', { days: m.estimatedDays }) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <Badge tone={m.status === 'active' ? 'moss' : 'neutral'}>
                    {t(`admin.shipping.statuses.${m.status}`)}
                  </Badge>
                  {manage && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEdit(m)}>
                        {t('common.edit')}
                      </Button>
                      <ConfirmButton
                        message={t('admin.shipping.deleteConfirm')}
                        onConfirm={() => del.mutateAsync(m.id)}
                      >
                        {t('common.delete')}
                      </ConfirmButton>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          <Section title={t('admin.shipping.trackingTitle')} description={t('admin.shipping.trackingLead')}>
            <p className="text-sm text-ink-2">{t('admin.shipping.carrierApiNote')}</p>
          </Section>
          {manage && <GeliverSection />}
        </>
      )}
      {edit && storeId && (
        <ShippingMethodDialog
          storeId={storeId}
          storeCurrency={storeCurrency}
          marketCountries={marketCountries}
          method={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
        />
      )}
    </PageFrame>
  );
}

/**
 * Geliver ile kargo (F7-28): bağlantı durumu, gönderici adresi (Geliver'deki adreslerden) ve varsayılan koli.
 * İşletme düzeyindedir (tüm mağazalar aynı Geliver hesabını kullanır).
 */
function GeliverSection() {
  const t = useT();
  const q = useQuery({
    queryKey: ['geliver-settings'],
    queryFn: () => api.get<GeliverSettingsView>('/v1/admin/shipping/geliver'),
  });
  const senders = useQuery({
    queryKey: ['geliver-senders'],
    queryFn: () => api.get<{ items: GeliverSender[] }>('/v1/admin/shipping/geliver/senders'),
    enabled: !!q.data?.connected,
    retry: false,
  });
  const [f, setF] = useState({
    senderAddressId: '',
    lengthCm: '30',
    widthCm: '20',
    heightCm: '15',
    defaultWeightGrams: '1000',
  });
  const s = q.data?.settings;
  useEffect(() => {
    if (!s) return;
    setF({
      senderAddressId: s.senderAddressId ?? '',
      lengthCm: String(s.lengthCm),
      widthCm: String(s.widthCm),
      heightCm: String(s.heightCm),
      defaultWeightGrams: String(s.defaultWeightGrams),
    });
  }, [s]);
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/shipping/geliver', {
        senderAddressId: f.senderAddressId || null,
        lengthCm: Number(f.lengthCm),
        widthCm: Number(f.widthCm),
        heightCm: Number(f.heightCm),
        defaultWeightGrams: Number(f.defaultWeightGrams),
      }),
    { invalidate: [['geliver-settings']], success: t('common.saved'), silentFieldErrors: true },
  );
  const digits = (v: string) => v.replace(/\D/g, '');
  return (
    <Section
      title={t('admin.shipping.geliver.title')}
      description={t('admin.shipping.geliver.lead')}
      action={<HelpTip k="shipping.geliver" />}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.connected ? (
        <p className="flex flex-wrap items-center gap-3 text-sm text-ink-2">
          <Badge tone="amber">{t('admin.shipping.geliver.notConnected')}</Badge>
          <Link to="/entegrasyonlar" className="font-medium text-pine underline underline-offset-2">
            {t('admin.shipping.geliver.connect')}
          </Link>
        </p>
      ) : (
        <div className="space-y-4">
          <p className="flex flex-wrap items-center gap-2 text-sm text-ink-2">
            <Badge tone={q.data.test ? 'amber' : 'moss'}>
              {q.data.test ? t('admin.shipping.geliver.test') : t('admin.shipping.geliver.live')}
            </Badge>
            {q.data.test ? t('admin.shipping.geliver.testNote') : t('admin.shipping.geliver.liveNote')}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('admin.shipping.geliver.sender')}
              help={<HelpTip k="shipping.geliver_sender" />}
              description={
                senders.data && !senders.data.items.length
                  ? t('admin.shipping.geliver.sendersEmpty')
                  : t('admin.shipping.geliver.senderHint')
              }
              error={
                fieldError(save.error, 'senderAddressId') ??
                (senders.error instanceof ApiError ? senders.error.title : undefined)
              }
              className="sm:col-span-2"
            >
              {(p) => (
                <Select
                  {...p}
                  value={f.senderAddressId}
                  onChange={(e) => setF({ ...f, senderAddressId: e.target.value })}
                >
                  <option value="">{t('admin.shipping.geliver.senderNone')}</option>
                  {(senders.data?.items ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.isDefault ? t('admin.shipping.geliver.senderDefault', { name: a.name }) : a.name) +
                        (a.address ? ` — ${a.address}` : '')}
                    </option>
                  ))}
                  {f.senderAddressId && !senders.data?.items.some((a) => a.id === f.senderAddressId) && (
                    <option value={f.senderAddressId}>{f.senderAddressId}</option>
                  )}
                </Select>
              )}
            </Field>
            <fieldset className="sm:col-span-2">
              <legend className="mb-2 flex items-center gap-1 text-sm font-medium text-ink">
                {t('admin.shipping.geliver.parcel')} <HelpTip k="shipping.geliver_parcel" />
              </legend>
              <div className="grid grid-cols-3 gap-3">
                {(['lengthCm', 'widthCm', 'heightCm'] as const).map((k) => (
                  <Field
                    key={k}
                    label={t(`admin.shipping.geliver.${k.replace('Cm', '')}`)}
                    error={fieldError(save.error, k)}
                  >
                    {(p) => (
                      <Input
                        {...p}
                        inputMode="numeric"
                        className="tabular"
                        value={f[k]}
                        onChange={(e) => setF({ ...f, [k]: digits(e.target.value) })}
                      />
                    )}
                  </Field>
                ))}
              </div>
            </fieldset>
            <Field
              label={t('admin.shipping.geliver.defaultWeight')}
              help={<HelpTip k="shipping.geliver_weight" />}
              description={t('admin.shipping.geliver.defaultWeightHint')}
              error={fieldError(save.error, 'defaultWeightGrams')}
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="numeric"
                  className="tabular"
                  value={f.defaultWeightGrams}
                  onChange={(e) => setF({ ...f, defaultWeightGrams: digits(e.target.value) })}
                />
              )}
            </Field>
          </div>
          {save.error && !save.error.errors.length && (
            <p role="alert" className="text-sm text-rose">
              {save.error.title}
            </p>
          )}
          <div className="flex items-center gap-1">
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('common.save')}
            </Button>
            <HelpTip k="shipping.geliver_save" />
          </div>
        </div>
      )}
    </Section>
  );
}

interface Tier {
  upTo: string;
  price: string;
}

/** Ülke kodunun arayüz dilindeki adı (tarayıcının bölge adları; bilinmiyorsa kod). */
function useCountryName() {
  const { locale } = useI18n();
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    names = null;
  }
  return (code: string) => names?.of(code) ?? code;
}

function ShippingMethodDialog({
  storeId,
  storeCurrency,
  marketCountries,
  method,
  onClose,
}: {
  storeId: string;
  storeCurrency: string;
  marketCountries: string[];
  method: ShippingMethodRow | null;
  onClose: () => void;
}) {
  const t = useT();
  const countryName = useCountryName();
  const [f, setF] = useState({
    name: method?.name ?? '',
    carrier: method?.carrier ?? '',
    audience: method?.audience ?? 'all',
    rateType: method?.rateType ?? 'flat',
    price: minorToInput(method?.priceMinor ?? 0),
    freeOver: minorToInput(method?.freeOverMinor),
    codAvailable: method?.codAvailable ?? false,
    estimatedDays: method?.estimatedDays ?? '',
    handlingDaysMin: method?.handlingDaysMin?.toString() ?? '',
    handlingDaysMax: method?.handlingDaysMax?.toString() ?? '',
    transitDaysMin: method?.transitDaysMin?.toString() ?? '',
    transitDaysMax: method?.transitDaysMax?.toString() ?? '',
    status: method?.status ?? 'active',
    currency: method?.currency ?? storeCurrency,
    countryCodes: method?.countryCodes ?? ([] as string[]),
  });
  const countryOptions = [...new Set([...marketCountries, ...f.countryCodes])].sort();
  const days = (v: string) => (v.trim() === '' ? null : Number(v));
  const [tiers, setTiers] = useState<Tier[]>(
    (method?.weightTiers ?? []).map((x) => ({
      upTo: String(x.upToGrams),
      price: minorToInput(x.priceMinor),
    })),
  );
  const body = () => ({
    name: f.name,
    carrier: f.carrier || null,
    audience: f.audience,
    rateType: f.rateType,
    currency: f.currency,
    countryCodes: f.countryCodes,
    priceMinor: toMinor(f.price, f.currency) ?? 0,
    freeOverMinor: f.freeOver.trim() ? (toMinor(f.freeOver, f.currency) ?? null) : null,
    codAvailable: f.codAvailable,
    estimatedDays: f.estimatedDays.trim() || null,
    handlingDaysMin: days(f.handlingDaysMin),
    handlingDaysMax: days(f.handlingDaysMax),
    transitDaysMin: days(f.transitDaysMin),
    transitDaysMax: days(f.transitDaysMax),
    status: f.status,
    weightTiers:
      f.rateType === 'weight'
        ? tiers.map((x) => ({
            upToGrams: Number(x.upTo) || 0,
            priceMinor: toMinor(x.price, f.currency) ?? 0,
          }))
        : null,
  });
  const save = useApiMutation(
    () =>
      method
        ? api.patch(`/v1/admin/shipping-methods/${method.id}`, body(), { ifMatch: method.version })
        : api.post('/v1/admin/shipping-methods', { ...body(), storeId }),
    {
      invalidate: [['shipping-methods', storeId], ['readiness', storeId], ['setup']],
      success: t('common.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const err = save.error;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={method ? t('admin.shipping.edit') : t('admin.shipping.add')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field
          label={t('admin.shipping.name')}
          required
          error={fieldError(err, 'name')}
          description={t('admin.shipping.nameHint')}
        >
          {(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
        </Field>
        {countryOptions.length > 1 || f.countryCodes.length ? (
          <fieldset className="space-y-2">
            <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
              {t('admin.shipping.countries')}
              <HelpTip k="shipping.countries" />
            </legend>
            <p className="text-sm text-ink-3">{t('admin.shipping.countriesHint')}</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {countryOptions.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="accent-[var(--zt-pine)]"
                    checked={f.countryCodes.includes(c)}
                    onChange={(e) =>
                      setF({
                        ...f,
                        countryCodes: e.target.checked
                          ? [...f.countryCodes, c]
                          : f.countryCodes.filter((x) => x !== c),
                      })
                    }
                  />
                  {countryName(c)} ({c})
                </label>
              ))}
            </div>
            {fieldError(err, 'countryCodes') && (
              <p role="alert" className="text-sm text-rose">
                {fieldError(err, 'countryCodes')}
              </p>
            )}
          </fieldset>
        ) : null}
        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label={t('admin.shipping.carrier')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="shipping.carrier" />}
          >
            {(p) => (
              <Select {...p} value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })}>
                <option value="">—</option>
                {CARRIERS.map((c) => (
                  <option key={c} value={c}>
                    {t(`admin.shipping.carriers.${c}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.shipping.audience')} help={<HelpTip k="shipping.audience" />}>
            {(p) => (
              <Select
                {...p}
                value={f.audience}
                onChange={(e) => setF({ ...f, audience: e.target.value as typeof f.audience })}
              >
                {(['all', 'retail', 'dealers'] as const).map((a) => (
                  <option key={a} value={a}>
                    {t(`admin.shipping.audiences.${a}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.shipping.currency')}
            help={<HelpTip k="shipping.currency" />}
            description={
              f.currency !== storeCurrency
                ? t('admin.shipping.currencyMismatch', { currency: storeCurrency })
                : undefined
            }
          >
            {(p) => (
              <Select {...p} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
                {['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.shipping.rateType')} help={<HelpTip k="shipping.rate_type" />}>
            {(p) => (
              <Select
                {...p}
                value={f.rateType}
                onChange={(e) => {
                  const v = e.target.value as typeof f.rateType;
                  setF({ ...f, rateType: v });
                  if (v === 'weight' && !tiers.length) setTiers([{ upTo: '1000', price: '' }]);
                }}
              >
                <option value="flat">{t('admin.shipping.rateTypes.flat')}</option>
                <option value="weight">{t('admin.shipping.rateTypes.weight')}</option>
              </Select>
            )}
          </Field>
          {f.rateType === 'flat' && (
            <Field
              label={t('admin.shipping.price')}
              error={fieldError(err, 'priceMinor')}
              description={t('admin.shipping.priceHint')}
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="decimal"
                  className="tabular"
                  value={f.price}
                  onChange={(e) => setF({ ...f, price: e.target.value })}
                />
              )}
            </Field>
          )}
        </div>
        {f.rateType === 'weight' && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('admin.shipping.tiers')}</legend>
            <p className="text-[0.8125rem] text-ink-3">{t('admin.shipping.tiersHint')}</p>
            {tiers.map((x, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                <Field label={t('admin.shipping.upToGrams')}>
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="numeric"
                      className="tabular"
                      value={x.upTo}
                      onChange={(e) =>
                        setTiers(
                          tiers.map((y, j) =>
                            j === i ? { ...y, upTo: e.target.value.replace(/\D/g, '') } : y,
                          ),
                        )
                      }
                    />
                  )}
                </Field>
                <Field label={t('admin.shipping.price')}>
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="decimal"
                      className="tabular"
                      value={x.price}
                      onChange={(e) =>
                        setTiers(tiers.map((y, j) => (j === i ? { ...y, price: e.target.value } : y)))
                      }
                    />
                  )}
                </Field>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t('common.remove')}
                  onClick={() => setTiers(tiers.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ))}
            {fieldError(err, 'weightTiers') && (
              <p role="alert" className="text-[0.8125rem] font-medium text-rose">
                {fieldError(err, 'weightTiers')}
              </p>
            )}
            <Button size="sm" onClick={() => setTiers([...tiers, { upTo: '', price: '' }])}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.shipping.addTier')}
            </Button>
          </fieldset>
        )}
        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label={t('admin.shipping.freeOver')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="shipping.free_over" />}
            error={fieldError(err, 'freeOverMinor')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                className="tabular"
                value={f.freeOver}
                onChange={(e) => setF({ ...f, freeOver: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.shipping.estimatedDays')}
            optionalLabel={t('common.optional')}
            description={t('admin.shipping.estimatedDaysHint')}
          >
            {(p) => (
              <Input
                {...p}
                value={f.estimatedDays}
                maxLength={20}
                onChange={(e) => setF({ ...f, estimatedDays: e.target.value })}
              />
            )}
          </Field>
          <fieldset className="grid grid-cols-2 gap-3">
            <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
              {t('admin.shipping.handlingDays')}
              <HelpTip k="shipping.handling_days" />
            </legend>
            {(['handlingDaysMin', 'handlingDaysMax'] as const).map((k) => (
              <Field
                key={k}
                label={t(k === 'handlingDaysMin' ? 'admin.shipping.daysMin' : 'admin.shipping.daysMax')}
                optionalLabel={t('common.optional')}
                error={fieldError(err, k)}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={0}
                    max={30}
                    inputMode="numeric"
                    value={f[k]}
                    onChange={(e) => setF({ ...f, [k]: e.target.value })}
                  />
                )}
              </Field>
            ))}
          </fieldset>
          <fieldset className="grid grid-cols-2 gap-3">
            <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
              {t('admin.shipping.transitDays')}
              <HelpTip k="shipping.transit_days" />
            </legend>
            {(['transitDaysMin', 'transitDaysMax'] as const).map((k) => (
              <Field
                key={k}
                label={t(k === 'transitDaysMin' ? 'admin.shipping.daysMin' : 'admin.shipping.daysMax')}
                optionalLabel={t('common.optional')}
                error={fieldError(err, k)}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={0}
                    max={60}
                    inputMode="numeric"
                    value={f[k]}
                    onChange={(e) => setF({ ...f, [k]: e.target.value })}
                  />
                )}
              </Field>
            ))}
          </fieldset>
          <Field label={t('admin.shipping.status')}>
            {(p) => (
              <Select
                {...p}
                value={f.status}
                onChange={(e) => setF({ ...f, status: e.target.value as typeof f.status })}
              >
                <option value="active">{t('admin.shipping.statuses.active')}</option>
                <option value="inactive">{t('admin.shipping.statuses.inactive')}</option>
              </Select>
            )}
          </Field>
        </div>
        <div className="flex items-start gap-3">
          <Switch
            id="cod-available"
            checked={f.codAvailable}
            onCheckedChange={(v) => setF({ ...f, codAvailable: v })}
          />
          <div>
            <label htmlFor="cod-available" className="font-medium">
              {t('admin.shipping.codAvailable')}
            </label>
            <p className="text-sm text-ink-2">{t('admin.shipping.codAvailableHint')}</p>
          </div>
        </div>
        {err && !err.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {err.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}
